import { useEffect, useRef, useState } from 'react';
import { can, ROLE_DESCRIPTIONS, ROLE_LABELS, type Role } from '@aq/shared';
import { useAuth } from '../auth/AuthContext.tsx';
import { Avatar } from '../../components/Avatar.tsx';
import { Button, Card, Field, Icons, PageHeader, Spinner } from '../../components/ui.tsx';
import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { api, ApiError } from '../../lib/api.ts';
import { forgetAvatar } from '../../lib/avatar-source.ts';
import { formatDateTime } from '../../lib/display.ts';
import {
  logoUrl,
  useBranding,
  useClearAvatar,
  useClearLogo,
  useUpdateProfile,
  useUploadAvatar,
  useUploadLogo,
} from '../../lib/profile-queries.ts';
import { TimeZoneSelect, offsetLabel } from './TimeZoneSelect.tsx';
import type { User } from '../../lib/types.ts';

/**
 * Your own account.
 *
 * Everything here is about the person looking at it, with one exception: an
 * owner can change the system's logo from the bottom of this page. It sits
 * here rather than in a settings screen of its own because it is the only
 * appearance setting there is, and a page with one control on it is a page
 * nobody finds.
 */
export function ProfilePage() {
  const { user, reload } = useAuth();
  const toast = useToast();
  const update = useUpdateProfile();

  const [name, setName] = useState(user?.name ?? '');
  const [zone, setZone] = useState(user?.timeZone ?? 'UTC');
  const [email, setEmail] = useState(user?.email ?? '');

  /**
   * Whether this person may change the address they sign in with.
   *
   * The label has always said an owner can, and for everyone including the
   * owner the box was read-only — the one person it named could not use it.
   * It goes through the users endpoint rather than /api/profile, because
   * /api/profile is open to every role and an address is not a preference.
   */
  const ownsAccounts = user?.role === 'OWNER';

  // The server is the source of truth; if it changes underneath us — another
  // tab, a fresh login — the fields follow rather than holding a stale draft.
  useEffect(() => {
    if (user) {
      setName(user.name);
      setZone(user.timeZone ?? 'UTC');
      setEmail(user.email);
    }
  }, [user]);

  if (!user) return null;

  const trimmedEmail = email.trim().toLowerCase();
  const emailChanged = ownsAccounts && trimmedEmail !== user.email;
  const dirty = name.trim() !== user.name || zone !== (user.timeZone ?? 'UTC') || emailChanged;

  async function save() {
    try {
      await update.mutateAsync({ name: name.trim(), timeZone: zone });
      // Second, and only when it changed: the address is the sign-in
      // identity, so a rejected one must not quietly take the name with it.
      if (emailChanged) await api.post(`/api/users/${user!.id}`, { email: trimmedEmail });
      await reload();
      toast.success(emailChanged ? 'Profile saved. Sign in with the new address from now on.' : 'Profile saved');
    } catch (caught) {
      toast.error(
        'Could not save your profile',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  return (
    <div className="h-full overflow-y-auto pb-10">
      <PageHeader
        title="Your profile"
        description="Your name, picture and the timezone every date in the system is shown in."
      />

      <div className="mt-4 space-y-4">
        <Card title="Picture" description="Shown beside your name across the system.">
          <AvatarEditor user={user} />
        </Card>

        <Card
          title="Details"
          actions={
            <Button
              variant="primary"
              onClick={() => void save()}
              disabled={!dirty || update.isPending}
            >
              {update.isPending ? <Spinner label="Saving" /> : 'Save changes'}
            </Button>
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
            />

            <div>
              <label
                htmlFor="profile-email"
                className="mb-1.5 flex items-baseline justify-between gap-2"
              >
                <span className="text-meta font-medium text-ink">Email</span>
                <span className="text-micro text-muted">
                  {ownsAccounts ? 'This is what you sign in with' : 'An owner can change this'}
                </span>
              </label>
              <input
                id="profile-email"
                type="email"
                value={ownsAccounts ? email : user.email}
                readOnly={!ownsAccounts}
                onChange={(event) => setEmail(event.target.value)}
                maxLength={255}
                className={
                  ownsAccounts
                    ? 'h-control w-full rounded-md border border-border bg-surface px-3 text-body-sm text-ink'
                    : 'h-control w-full cursor-not-allowed rounded-md border border-border bg-sunken px-3 text-body-sm text-muted'
                }
              />
            </div>

            <div>
              <label
                htmlFor="profile-timezone"
                className="mb-1.5 flex items-baseline justify-between gap-2"
              >
                <span className="text-meta font-medium text-ink">Timezone</span>
                <span className="text-micro text-muted">{offsetLabel(zone)}</span>
              </label>
              <TimeZoneSelect id="profile-timezone" value={zone} onChange={setZone} />
              <p className="mt-1.5 text-micro text-muted">
                Right now that reads {formatInZone(zone)}.
              </p>
            </div>

            <div>
              <span className="mb-1.5 block text-meta font-medium text-ink">Role</span>
              <div className="rounded-md border border-border bg-sunken px-3 py-2">
                <p className="text-body-sm font-medium text-ink">
                  {ROLE_LABELS[user.role as Role]}
                </p>
                <p className="mt-0.5 text-micro text-muted">
                  {ROLE_DESCRIPTIONS[user.role as Role]}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {can(user.role as Role, 'settings:write') ? <LogoEditor /> : null}
      </div>
    </div>
  );
}

function AvatarEditor({ user }: { user: User }) {
  const { reload } = useAuth();
  const toast = useToast();
  const dialog = useDialog();
  const upload = useUploadAvatar();
  const clear = useClearAvatar();
  const input = useRef<HTMLInputElement | null>(null);

  async function choose(file: File) {
    try {
      await upload.mutateAsync(file);
      // Drop the cached copy before reloading: the profile's version stamp
      // changes too, but only once the new profile has been read back, and
      // the sidebar re-renders before that.
      forgetAvatar(user.id);
      await reload();
      toast.success('Picture updated');
    } catch (caught) {
      toast.error(
        'Could not upload that picture',
        caught instanceof ApiError
          ? caught.message
          : 'Use a PNG, JPEG, WebP, GIF or SVG under 4 MB.',
      );
    }
  }

  async function remove() {
    const confirmed = await dialog.confirm({
      title: 'Remove your picture?',
      message: 'Your initials will be shown instead. You can upload a new one at any time.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      await clear.mutateAsync();
      forgetAvatar(user.id);
      await reload();
      toast.success('Picture removed');
    } catch {
      toast.error('Could not remove the picture');
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-5">
      <Avatar
        name={user.name}
        userId={user.id}
        hasAvatar={user.hasAvatar}
        version={user.updatedAt}
        size="xl"
      />

      <div className="min-w-0">
        <div className="flex flex-wrap gap-2">
          <Button
            icon={<Icons.Upload />}
            onClick={() => input.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? (
              <Spinner label="Uploading" />
            ) : user.hasAvatar ? (
              'Replace'
            ) : (
              'Upload a picture'
            )}
          </Button>
          {user.hasAvatar ? (
            <Button variant="ghost" icon={<Icons.Trash />} onClick={() => void remove()}>
              Remove
            </Button>
          ) : null}
        </div>
        <p className="mt-2 text-micro text-muted">
          PNG, JPEG, WebP, GIF or SVG, up to 4 MB. Square images look best — anything else is
          cropped to the centre.
        </p>
      </div>

      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void choose(file);
        }}
      />
    </div>
  );
}

function LogoEditor() {
  const branding = useBranding();
  const upload = useUploadLogo();
  const clear = useClearLogo();
  const toast = useToast();
  const dialog = useDialog();
  const input = useRef<HTMLInputElement | null>(null);

  const current = logoUrl(branding.data);

  async function choose(file: File) {
    try {
      await upload.mutateAsync(file);
      toast.success('Logo updated', 'It appears in the sidebar and on the sign-in page.');
    } catch (caught) {
      toast.error(
        'Could not upload that logo',
        caught instanceof ApiError
          ? caught.message
          : 'Use a PNG, JPEG, WebP, GIF or SVG under 4 MB.',
      );
    }
  }

  async function remove() {
    const confirmed = await dialog.confirm({
      title: 'Restore the default logo?',
      message: 'The mark this system shipped with will be shown instead.',
      confirmLabel: 'Restore default',
    });
    if (!confirmed) return;

    try {
      await clear.mutateAsync();
      toast.success('Default logo restored');
    } catch {
      toast.error('Could not remove the logo');
    }
  }

  return (
    <Card
      title="System logo"
      description="Shown in the sidebar and on the sign-in page. Everyone sees this one."
    >
      <div className="flex flex-wrap items-center gap-5">
        <div className="flex h-20 w-40 items-center justify-center rounded-lg border border-border bg-sunken p-2">
          <img
            src={current ?? '/brand/logo.png'}
            alt="Current logo"
            className="max-h-full max-w-full object-contain"
          />
        </div>

        <div>
          <div className="flex flex-wrap gap-2">
            <Button
              icon={<Icons.Upload />}
              onClick={() => input.current?.click()}
              disabled={upload.isPending}
            >
              {upload.isPending ? <Spinner label="Uploading" /> : 'Upload a logo'}
            </Button>
            {branding.data?.hasLogo ? (
              <Button variant="ghost" onClick={() => void remove()}>
                Restore default
              </Button>
            ) : null}
          </div>
          <p className="mt-2 max-w-md text-micro text-muted">
            A wide image works best — it is fitted into the sidebar header without cropping. SVG or
            a transparent PNG keeps the edges clean on both light and dark backgrounds.
          </p>
        </div>
      </div>

      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void choose(file);
        }}
      />
    </Card>
  );
}

function formatInZone(zone: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(new Date());
  } catch {
    return formatDateTime(new Date());
  }
}
